<script lang="ts">
    import TutorialVideo from '@/components/tutorial-video.vue';
    import Vue from 'vue';

    export default Vue.extend({
        head: {
			title: { inner: 'Tennis Court Booking Management for your Club', separator: '|', complement: 'Str8 Sets' },
            meta: [
				{ name: 'description', content: 'Give your members the freedom to play tennis when they want, booking a court via the app. No matter how complex your court booking rules, Str8 Sets can handle them. Pricing by member/non-member, member type, court group, peak/off-peak/lights and more.', id: 'desc'},
                { property: 'og:type', content: 'website' },
				{ property: 'og:url', content: 'https://www.str8sets.com/tennis_court_bookings' },
                { property: 'og:title', content: 'Tennis Court Booking Management for your Club | Str8 Sets' },
                { property: 'og:description', content: 'Give your members the freedom to play tennis when they want, booking a court via the app. No matter how complex your court booking rules, Str8 Sets can handle them. Pricing by member/non-member, member type, court group, peak/off-peak/lights and more.' },
                { property: 'og:image', content: 'https://www.str8sets.com/img/meta_str8sets_tennis_1200x628.jpg' },   
				{ property: 'og:image:alt', content: 'Str8 Sets Tennis Logo and Screenshot' },            
                { name: 'twitter:site', content: 'str8sets' },
				{ name: 'twitter:creator', content: '@str8sets' },
				{ name: 'twitter:card', content: 'summary_large_image' },  
				{ name: 'twitter:url', content: 'https://www.str8sets.com/tennis_court_bookings' },
				{ name: 'twitter:title', content: 'Tennis Court Booking Management for your Club | Str8 Sets' },
                { name: 'twitter:description', content: 'Give your members the freedom to play tennis when they want, booking a court via the app. No matter how complex your court booking rules, Str8 Sets can handle them. Pricing by member/non-member, member type, court group, peak/off-peak/lights and more.' },
				{ name: 'twitter:image', content: 'https://www.str8sets.com/img/meta_str8sets_tennis_1200x628.jpg' },
				{ name: 'twitter:image:alt', content: 'Str8 Sets Tennis Logo and Screenshot' }
            ],
            link: [
      			{ rel: 'canonical', href: 'https://www.str8sets.com/tennis_court_bookings', id: 'canonical' }
			]
        },
        components: {
            'tutorial-video': TutorialVideo
        }
    })
</script>

<template>
    <div>    
        <div class="parallax image-clay-court height-350">
            <div class="container">
                <div class="feature-page-header">
                    <h1>Court Bookings</h1>
                    <h2>Booking a court has never been easier!</h2>
                </div>
                <div class="row">
                    <router-link class="clear_button" to="/case-studies_clubs" role="button">VIEW CLUB CASE STUDIES</router-link>
                    <router-link class="clear_button" to="/faq#courtbooking" role="button">FREQUENT QUESTIONS</router-link>
                </div>
            </div>
        </div>

        <section class="feature-list section">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <p><strong>Give your members the freedom to play when they want</strong>, knowing that a court will be available for them with a couple of clicks 
                                on the app. Str8 Sets Court Bookings achieves that and more. Courts are now only a few clicks away from being booked by a member (or non-member) 
                                and as a club manager, you can decide on the booking policies for the club using Str8 Sets' flexible business rules.</p>
                            <p>Court utilisation at your club will be maximised by Str8 Sets Court Bookings.</p>
                        </div>
                    </div>
                </div>
                <div class="spacer-large"></div>
                <div class="row">
                    <div class="col-md-12 heading">
                        <img class="icon" src="@/assets/images/courts.svg" alt="Green Court Bookings Icon" data-no-retina>
                        <h2>How does Court Bookings work?</h2>
                        <h4>Configuration made simple</h4>
                    </div>                            
                </div>
                <div class="row">                               
                    <div class="col-md-6">
                        <div class="content mt-100">
                            <p>Whether you charge for court bookings or not, and whether you allow non-members to book courts or not, <strong>Str8 Sets can handle your business rules no matter how complex</strong>. 
                                You decide how long members or non-members can book. You also decide what courts are bookable, and when.</p>
                            <p>With Str8 Sets you can even decide on different prices for different times in a day and for different membership tiers. Setup your courts, opening hours and other 
                                rules via the club manager portal (Club Dashboard) then enable the feature and you’re good to go.</p>
                            <p>With thousands of court bookings already on Str8 Sets, it’s the simplest and safest way to manage your courts.</p>
                        </div>
                    </div>
                    <div class="col-md-6 text-center">
                        <img class="img_monitor zoom-hover" :src="'/img/dashboard-court-bookings.png'" alt="Str8 Sets Dashboard Screenshot (Court Bookings)" v-rjs="2">
                    </div>
                </div>
                <div class="spacer-large"></div>
                <div class="row">
                    <div class="col-md-12 heading">
                        <img class="icon" src="@/assets/images/insights.svg" alt="Green Insights Icon" data-no-retina>
                        <h2>Booking Insights</h2>
                        <h4>Data is power!</h4>
                    </div>                            
                </div>
                <div class="row">                               
                    <div class="col-md-6">
                        <div class="content mt-100">
                            <p>You need to know how well your courts are utilized, when are your busiest hours, who books courts and several other topics related to your court bookings.</p>
                            <p>Our Insight reports allow you to know all that and more, and presents this information intuitively in different formats. This knowledge is essential for anyone wanting 
                                to measure their utilization and make data-informed decisions that can improve court utilization.</p>
                            <p>Insights also enables you to come up with creative ideas for increasing court utilization during your off-peak times.</p>
                        </div>
                    </div>
                    <div class="col-md-6 text-center">
                        <img class="img_monitor zoom-hover" :src="'/img/dashboard-insights.png'" alt="Str8 Sets Dashboard Screenshot (Court Booking Insights)" v-rjs="2">
                    </div>
                </div>
            </div>
        </section>

        <div class="container"> 
            <tutorial-video
                :videoVimeoId=663539747
                title="Creating a Court Booking"
                bodyText="Learn how quick and easy it is to create a Court Booking via the Str8 Sets app.">
            </tutorial-video>
        </div>

        <div class="parallax image-clay-court height-150" ></div>

        <section class="feature-list section">
            <div class="container">
                <div class="row">
                    <div class="col-md-12 heading">
                        <img class="icon" src="@/assets/images/matcha.svg" alt="Green Matcha Icon" data-no-retina>
                        <h2>Court Booking and Matcha</h2>
                        <h4>Complete integration</h4>
                    </div>
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <p>In keeping with our philosophy of offering you a single medium for managing your club, we integrated 
                            court bookings with Matcha. Hence, allowing your members to request a hit and have a court ready for them in 
                            one easy process. As a club manager, you can decide on how long a court should remain booked if a hit is 
                            not taken.</p>
                        </div>                            
                    </div>
                </div>
            </div>
            <div class="container">
                <div class="spacer-large"></div>
                <div class="row text-center">
                    <img class="img_inrow" :src="'/img/android_courtavailability_243x440.jpg'" alt="Str8 Sets App Screenshot (Android, Court Bookings Court Availability)" v-rjs="2" />
                    <img class="img_inrow" :src="'/img/android_courtbookingnew_243x440.jpg'" alt="Str8 Sets App Screenshot (Android, Court Booking New)" v-rjs="2" />
                    <img class="img_inrow" :src="'/img/android_courtbookings_243x440.jpg'" alt="Str8 Sets App Screenshot (Android, Court Bookings Home)" v-rjs="2" />
                </div> 
            </div>
        </section>

        <div class="parallax image-clay-court height-150" ></div>

        <section class="feature-list section">
            <div class="container">
                <div class="row">
                    <div class="col-md-12">
                        <div class="content mt-100">
                            <div class="text-heading">
                                <h2>Privacy</h2>
                            </div> 
                            <p>We care a lot about your members’ privacy. Therefore, only a club member who booked a court and/or the club manager can see the given member’s booking details. 
                                Only the person who made the booking or the club manager can cancel that booking.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="spacer-large"></div>
        </section>
    </div>
</template>